import { CheckIcon, CreditCardIcon, LoaderCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { ApiError, errorMessage } from '@/api/client'
import {
  useBilling,
  useCancelSubscription,
  useCheckout,
  useMe,
  usePaymentMethods,
  useResumeSubscription,
  useUndoScheduledChange,
  useUpcoming,
} from '@/api/queries'
import type { BillingSummary, CheckoutSession, UpcomingInvoice } from '@/api/types'
import { cardLabel } from '@/lib/cards'
import { BillingDetailsCard } from '@/components/billing/BillingDetailsCard'
import { ChangePlanDialog } from '@/components/billing/ChangePlanDialog'
import { EmbeddedCheckoutDialog } from '@/components/billing/EmbeddedCheckoutDialog'
import { PaymentMethodsCard } from '@/components/billing/PaymentMethodsCard'
import { PageBody, PageHeader } from '@/components/AppShell'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  centsLabel,
  formatUsd,
  hasLiveSubscription,
  PLAN_PRICES,
  planIncludes,
  planRank,
  priceLabel,
  SEAT_PRICE,
  seatPriceLabel,
  SELLABLE_PLANS,
  statusLabel,
  stripeReturn,
  subscriptionActivated,
  type BillingInterval,
} from '@/lib/billing'
import { hasBilling } from '@/lib/access'
import { formatAbsolute } from '@/lib/format'
import {
  intervalName,
  liveCardAction,
  planErrorText as planChangeErrorText,
  planName,
  subscribeConfirmText,
} from '@/lib/plan-change'
import { cn } from '@/lib/utils'
import { useReadOnly } from '@/staff/hooks'

/** How long the return page waits for the webhook before saying it is slow. */
const ACTIVATION_WAIT_MS = 60_000

/**
 * The PLAN page: what the account is subscribed to, and how to change it.
 *
 * <h2>Two billing pages, two meanings</h2>
 * This page (`/app/plan`, "Plan" in the sidebar) is the **subscription**: Free, Solo or Team, paid
 * through Stripe Embedded Checkout (mounted in a dialog, never a redirect) and managed entirely in this
 * page: change, cancel, cards, billing details (2026-10-BILLING-IN-APP). The **police balance** page
 * (`/app/billing`, `BillingPage`) is prepaid money for managed police. They share a Stripe
 * customer and nothing else.
 *
 * <h2>The page never grants anything</h2>
 * Coming back from Checkout (`?checkout=success`) only starts polling `GET /api/billing`. The plan
 * changes when the webhook lands server-side, and the page shows "Activating your subscription…"
 * until it does.
 */
export function PlanPage() {
  const me = useMe()
  const location = useLocation()
  const returned = stripeReturn(location.search)
  const awaiting = returned?.kind === 'checkout' && returned.outcome === 'success'
  const [waitedTooLong, setWaitedTooLong] = useState(false)
  const [startedAt] = useState(() => Date.now())

  const isAdmin = hasBilling({ role: me.data?.role, billing: me.data?.billing })
  const billing = useBilling({
    enabled: isAdmin,
    refetchMs: awaiting && !waitedTooLong ? 2_000 : false,
  })
  const activated = billing.data !== undefined && subscriptionActivated(billing.data)

  useEffect(() => {
    if (!awaiting || activated) return
    const id = window.setTimeout(() => {
      setWaitedTooLong(true)
    }, Math.max(0, ACTIVATION_WAIT_MS - (Date.now() - startedAt)))
    return () => {
      window.clearTimeout(id)
    }
  }, [awaiting, activated, startedAt])

  if (me.isPending) {
    return (
      <>
        <PageHeader title="Plan" />
        <PageBody>
          <TableSkeleton rows={3} />
        </PageBody>
      </>
    )
  }

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Plan" description="Your account's subscription" />
        <PageBody>
          <Alert>
            <AlertTitle>Billing access only</AlertTitle>
            <AlertDescription>
              The plan is managed by account administrators and members with billing access.
            </AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Plan"
        description={
          <>
            Your subscription: what your account includes, and how to change it. Prepaid money for
            managed police lives on the{' '}
            <Link to="/app/billing" className="text-primary underline-offset-4 hover:underline">
              police balance
            </Link>{' '}
            page.
          </>
        }
      />
      <PageBody>
        {awaiting && !activated ? (
          waitedTooLong ? (
            <Alert variant="warning" data-testid="checkout-slow">
              <AlertTitle>Still waiting for Stripe</AlertTitle>
              <AlertDescription>
                Your payment is taking longer than usual to confirm. Nothing is lost: this page
                updates when the confirmation arrives, so reload it in a minute.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="info" data-testid="checkout-activating">
              <LoaderCircleIcon className="animate-spin" />
              <AlertTitle>Activating your subscription…</AlertTitle>
              <AlertDescription>
                Payment received. We are waiting for Stripe to confirm it, which takes a few seconds.
              </AlertDescription>
            </Alert>
          )
        ) : null}
        {awaiting && activated ? (
          <Alert variant="info" data-testid="checkout-done">
            <CheckIcon />
            <AlertTitle>Subscription active</AlertTitle>
            <AlertDescription>Welcome to {billing.data?.plan_name}.</AlertDescription>
          </Alert>
        ) : null}

        {billing.isPending ? (
          <TableSkeleton rows={3} />
        ) : billing.isError ? (
          <ErrorState
            error={billing.error}
            title="Could not load your plan"
            onRetry={() => {
              void billing.refetch()
            }}
          />
        ) : (
          <>
            <CurrentPlan billing={billing.data} />
            <Plans billing={billing.data} />
            {billing.data.billing_enabled ? <BillingSections /> : null}
          </>
        )}
      </PageBody>
    </>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  )
}

function BillingSections() {
  const readOnly = useReadOnly()
  return (
    <>
      <PaymentMethodsCard readOnly={readOnly} />
      <BillingDetailsCard readOnly={readOnly} />
    </>
  )
}

function CurrentPlan({ billing }: { billing: BillingSummary }) {
  const readOnly = useReadOnly()
  const paid = billing.plan_code !== 'free'
  const isComp = billing.source === 'staff'
  const live = hasLiveSubscription(billing)
  const upcoming = useUpcoming(live)
  const cancel = useCancelSubscription()
  const resume = useResumeSubscription()
  const undo = useUndoScheduledChange()
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const scheduled = upcoming.data?.scheduled_change ?? null
  const actionError = cancel.error ?? resume.error ?? undo.error

  return (
    <Card data-testid="current-plan">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <CreditCardIcon className="size-4 text-primary" />
          <span data-testid="current-plan-name">{billing.plan_name}</span>
          {isComp ? <Badge variant="secondary">complimentary</Badge> : null}
          {live || billing.status !== 'none' ? (
            <Badge variant={billing.status === 'active' ? 'default' : 'outline'} data-testid="plan-status">
              {statusLabel(billing.status)}
            </Badge>
          ) : null}
        </CardTitle>
        <CardDescription data-testid="plan-includes">{planIncludes(billing.plan_code)}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!billing.billing_enabled ? (
          <Alert>
            <AlertDescription>Billing is not enabled on this server, so plans cannot be bought here.</AlertDescription>
          </Alert>
        ) : null}
        {billing.status === 'past_due' ? (
          <Alert variant="warning">
            <AlertTitle>Your last payment failed</AlertTitle>
            <AlertDescription>
              You keep your plan while Stripe retries the card. Pay the open invoice from{' '}
              <Link to="/app/invoices" className="font-medium underline">
                Invoices
              </Link>
              , or update your card below, to avoid a downgrade.
            </AlertDescription>
          </Alert>
        ) : null}
        {billing.cancel_at_period_end && billing.current_period_end !== null ? (
          <Alert variant="warning" data-testid="plan-cancelling">
            <AlertTitle>Cancels on {formatAbsolute(billing.current_period_end)}</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>You keep {billing.plan_name} until then; after that the account returns to Free.</span>
              {readOnly ? null : (
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={resume.isPending}
                    data-testid="resume-subscription"
                    onClick={() => {
                      cancel.reset()
                      undo.reset()
                      resume.mutate()
                    }}
                  >
                    {resume.isPending ? 'Resuming…' : 'Resume subscription'}
                  </Button>
                </span>
              )}
            </AlertDescription>
          </Alert>
        ) : null}
        {scheduled !== null ? (
          <Alert variant="info" data-testid="scheduled-change">
            <AlertTitle>
              Changing to {planName(scheduled.plan_code)} ({intervalName(scheduled.interval)}) on{' '}
              {formatAbsolute(scheduled.effective_at)}
            </AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>Nothing is charged today; you keep your current plan until then.</span>
              {readOnly ? null : (
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={undo.isPending}
                    data-testid="undo-scheduled-change"
                    onClick={() => {
                      cancel.reset()
                      resume.reset()
                      undo.mutate()
                    }}
                  >
                    {undo.isPending ? 'Undoing…' : 'Undo'}
                  </Button>
                </span>
              )}
            </AlertDescription>
          </Alert>
        ) : null}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <Fact label="Billing interval">
            {billing.interval === null ? '—' : billing.interval === 'month' ? 'Monthly' : 'Annual'}
          </Fact>
          {billing.plan_code === 'team' ? <Fact label="Extra seats">{billing.extra_seats}</Fact> : null}
          {live && billing.current_period_end !== null ? (
            <Fact label={billing.cancel_at_period_end ? 'Ends' : 'Renews'}>
              {formatAbsolute(billing.current_period_end)}
            </Fact>
          ) : null}
          {live ? <NextInvoice upcoming={upcoming.data} /> : null}
          {isComp && billing.current_period_end !== null ? (
            <Fact label="Complimentary until">{formatAbsolute(billing.current_period_end)}</Fact>
          ) : null}
          {!paid ? <Fact label="Price">Free for ever</Fact> : null}
        </dl>

        {actionError === null ? null : (
          <Alert variant="destructive" data-testid="plan-action-error">
            <AlertDescription>{planErrorText(actionError)}</AlertDescription>
          </Alert>
        )}
        {live && !billing.cancel_at_period_end && !readOnly ? (
          <div>
            <Button
              variant="outline"
              disabled={cancel.isPending}
              data-testid="cancel-subscription"
              onClick={() => {
                resume.reset()
                undo.reset()
                setConfirmingCancel(true)
              }}
            >
              Cancel subscription
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">
              You keep your plan until the end of the period you paid for. You can resume until then.
            </p>
          </div>
        ) : null}
      </CardContent>

      {confirmingCancel ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !cancel.isPending) setConfirmingCancel(false)
          }}
        >
          <DialogContent data-testid="cancel-dialog">
            <DialogHeader>
              <DialogTitle>Cancel your subscription?</DialogTitle>
              <DialogDescription>
                {billing.current_period_end === null
                  ? 'The subscription ends at the end of the current period.'
                  : `You keep ${billing.plan_name} until ${formatAbsolute(billing.current_period_end)}, then the account returns to Free. Nothing is refunded and nothing more is charged.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" disabled={cancel.isPending} onClick={() => { setConfirmingCancel(false) }}>
                Keep subscription
              </Button>
              <Button
                variant="destructive"
                disabled={cancel.isPending}
                data-testid="confirm-cancel"
                onClick={() => {
                  cancel.mutate(undefined, {
                    onSettled: () => {
                      setConfirmingCancel(false)
                    },
                  })
                }}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel at period end'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </Card>
  )
}

function NextInvoice({ upcoming }: { upcoming: UpcomingInvoice | undefined }) {
  const next = upcoming?.next_invoice ?? null
  if (upcoming === undefined || next === null) return null
  return (
    <Fact label="Next invoice">
      <span data-testid="next-invoice">
        {centsLabel(next.total_cents)}
        {next.tax_cents > 0 ? ` (incl. ${centsLabel(next.tax_cents)} tax)` : ''}
        {next.date === null ? '' : ` on ${formatAbsolute(next.date)}`}
      </span>
    </Fact>
  )
}

function planErrorText(error: unknown): string {
  return planChangeErrorText(error instanceof ApiError ? error.code : undefined, errorMessage(error))
}

function Plans({ billing }: { billing: BillingSummary }) {
  const [interval, setInterval] = useState<BillingInterval>(billing.interval ?? 'month')
  const checkout = useCheckout()
  const readOnly = useReadOnly()
  const live = hasLiveSubscription(billing)
  const [pending, setPending] = useState<string | null>(null)
  const navigate = useNavigate()
  const [session, setSession] = useState<CheckoutSession | null>(null)
  const [changing, setChanging] = useState<{ plan: string; interval: BillingInterval } | null>(null)
  const upcoming = useUpcoming(live)
  const scheduled = upcoming.data?.scheduled_change ?? null
  // bug-224: with a default card on file the subscription is created server-side on it (no card form),
  // so the click is confirmed first; with none, Embedded Checkout collects one as before.
  const cards = usePaymentMethods(billing.billing_enabled && !live && !readOnly)
  const defaultCard = (cards.data ?? []).find((card) => card.is_default) ?? null
  const [confirming, setConfirming] = useState<string | null>(null)
  const [lastChoice, setLastChoice] = useState<string | null>(null)
  const isComp = billing.source === 'staff' && billing.plan_code !== 'free'
  const declined = checkout.error instanceof ApiError && checkout.error.code === 'payment_declined'

  function start(planCode: string, useCheckout = false) {
    setPending(planCode)
    setLastChoice(planCode)
    checkout.mutate(
      { planCode, interval, useCheckout },
      {
        onSuccess: (result) => {
          setConfirming(null)
          if (result.outcome === 'subscribed') {
            // Charged on the saved card; the plan lands with the webhook, as after Checkout.
            void navigate('/app/plan?checkout=complete')
          } else if (result.client_secret !== undefined && result.session_id !== undefined) {
            setSession({ client_secret: result.client_secret, session_id: result.session_id })
          }
        },
        onSettled: () => {
          setPending(null)
        },
      },
    )
  }

  function choose(planCode: string) {
    checkout.reset()
    if (defaultCard !== null) {
      setConfirming(planCode)
    } else {
      start(planCode)
    }
  }

  const liveAction = (code: string) =>
    liveCardAction(billing, { plan: code, interval }, scheduled === null ? null : scheduled)

  return (
    <section className="flex flex-col gap-4" aria-label="Plans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Plans</h2>
        <div role="group" aria-label="Billing interval" className="inline-flex rounded-md border border-border p-0.5">
          {(['month', 'year'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={interval === option}
              data-testid={`interval-${option}`}
              onClick={() => {
                setInterval(option)
              }}
              className={cn(
                'rounded px-3 py-1 text-sm',
                interval === option ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {option === 'month' ? 'Monthly' : 'Annual (2 months free)'}
            </button>
          ))}
        </div>
      </div>

      {checkout.isError && confirming === null ? (
        <Alert variant="destructive" data-testid="checkout-error">
          <AlertDescription className="flex flex-col gap-2">
            <span>{checkoutErrorText(checkout.error)}</span>
            {declined && lastChoice !== null ? (
              <span>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="subscribe-other-card"
                  onClick={() => {
                    start(lastChoice, true)
                  }}
                >
                  Pay with another card
                </Button>
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 md:grid-cols-3">
        <PlanCard
          code="free"
          name="Free"
          current={billing.plan_code === 'free'}
          price="$0"
          footnote="Free for ever. Hard limits, no trial clock."
        />
        {SELLABLE_PLANS.map((code) => {
          const current = billing.plan_code === code
          const lower = planRank(code) < planRank(billing.plan_code)
          return (
            <PlanCard
              key={code}
              code={code}
              name={code === 'solo' ? 'Solo' : 'Team'}
              current={current}
              price={priceLabel(code, interval) ?? ''}
              footnote={
                code === 'team'
                  ? `Includes 5 members; ${seatPriceLabel(interval)} (each active member beyond five).`
                  : undefined
              }
              action={
                readOnly || !billing.billing_enabled || billing.cancel_at_period_end ? null : live ? (
                  liveAction(code) === 'none' ? null : (
                    <Button
                      variant={liveAction(code) === 'upgrade' || liveAction(code) === 'keep' ? 'default' : 'outline'}
                      data-testid={`choose-${code}`}
                      onClick={() => {
                        setChanging({ plan: code, interval })
                      }}
                    >
                      {liveAction(code) === 'keep'
                        ? `Keep ${planName(code)}`
                        : current
                          ? `Switch to ${intervalName(interval)} billing`
                          : `${lower ? 'Change' : 'Upgrade'} to ${planName(code)}`}
                    </Button>
                  )
                ) : current ? null : (
                  <Button
                    disabled={pending !== null}
                    data-testid={`choose-${code}`}
                    onClick={() => {
                      choose(code)
                    }}
                  >
                    {pending === code
                      ? 'Working…'
                      : isComp
                        ? `Subscribe to ${planName(code)}`
                        : `${lower ? 'Change' : 'Upgrade'} to ${planName(code)}`}
                  </Button>
                )
              }
            />
          )
        })}
      </div>
      {session === null ? null : (
        <EmbeddedCheckoutDialog
          clientSecret={session.client_secret}
          title="Subscribe"
          description="Pay securely with Stripe, right here. Your plan activates as soon as Stripe confirms the payment."
          onComplete={() => {
            setSession(null)
            void navigate(`/app/plan?checkout=complete&session_id=${encodeURIComponent(session.session_id)}`)
          }}
          onClose={() => {
            setSession(null)
          }}
        />
      )}
      {confirming === null || defaultCard === null ? null : (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && pending === null) setConfirming(null)
          }}
        >
          <DialogContent data-testid="subscribe-confirm-dialog">
            <DialogHeader>
              <DialogTitle>
                Subscribe to {planName(confirming)} ({intervalName(interval)})?
              </DialogTitle>
              <DialogDescription data-testid="subscribe-confirm-text">
                {subscribeConfirmText({
                  plan: confirming,
                  interval,
                  price: priceLabel(confirming, interval),
                  card: cardLabel(defaultCard),
                  complimentaryPlan: isComp ? billing.plan_code : null,
                })}
              </DialogDescription>
            </DialogHeader>
            {checkout.isError ? (
              <Alert variant="destructive" data-testid="subscribe-confirm-error">
                <AlertDescription>{checkoutErrorText(checkout.error)}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                variant="ghost"
                disabled={pending !== null}
                onClick={() => {
                  start(confirming, true)
                }}
              >
                Use another card
              </Button>
              <Button
                disabled={pending !== null}
                data-testid="subscribe-confirm"
                onClick={() => {
                  start(confirming)
                }}
              >
                {pending === null ? 'Subscribe and pay' : 'Working…'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {changing === null ? null : (
        <ChangePlanDialog
          plan={changing.plan}
          interval={changing.interval}
          onClose={() => {
            setChanging(null)
          }}
        />
      )}
      <p className="text-xs text-muted-foreground">
        Prices in USD. Annual billing is ten months for twelve ({formatUsd(PLAN_PRICES['solo']?.year ?? 0)} Solo,{' '}
        {formatUsd(PLAN_PRICES['team']?.year ?? 0)} Team, +{formatUsd(SEAT_PRICE.year)} per extra seat). Tax is
        added at checkout where it applies.
      </p>
    </section>
  )
}

function checkoutErrorText(error: unknown): string {
  const code = error instanceof ApiError ? error.code : undefined
  switch (code) {
    case 'subscription_exists':
      return 'You already have a subscription. Reload the page to change it.'
    case 'payment_declined':
      return `The card was declined: ${errorMessage(error)} Nothing was charged.`
    case 'plan_not_purchasable':
      return 'That plan cannot be bought.'
    case 'billing_disabled':
    case 'stripe_unavailable':
      return 'Billing is unavailable right now. Try again shortly.'
    default:
      return errorMessage(error)
  }
}

function PlanCard({
  code,
  name,
  price,
  current,
  footnote,
  action,
}: {
  code: string
  name: string
  price: string
  current: boolean
  footnote?: string | undefined
  action?: React.ReactNode
}) {
  return (
    <Card className={cn('min-w-0', current && 'border-primary')} data-testid={`plan-card-${code}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          {name}
          {current ? <Badge>current</Badge> : null}
        </CardTitle>
        <p className="text-2xl font-semibold tabular-nums">{price}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{planIncludes(code)}</p>
        {footnote === undefined ? null : <p className="text-xs text-muted-foreground">{footnote}</p>}
        {action}
      </CardContent>
    </Card>
  )
}
